import React, { useEffect, useState } from 'react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { DashboardLayout } from '../../../components/layout';
import { AreaToolbar, AreaToolbarButton } from '../../../components/layout/area-toolbar';
import { StartPageTable } from '../../../components/start-page-table/start-page-table';
import { useMetaTitle } from '../../../hooks';
import { Restricted } from '../../../authentication';
import { ResourceOperations, ResourceTypes } from '../../../authentication/resources';
import { ClassroomDto, ClassroomDtoEx } from '../../../api-service/clients';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { attendanceAreaPathName, getClassroomCreatePath, getClassroomEditPath } from '../attendance-area-pathnames';
import { usePostToTab } from '../../search/hooks/usePostToTab';
import { classroomColumns } from './classroom-columns';
import { REFRESH_CLASSROOM_TABLE } from './classroom-uniform/classroom';
import { useAttendanceRights } from '../use-attendance-rights';

const classRoomClient = moduleClientFactory.createAttendanceClient();

export function ClassroomsPage() {
  const [classRooms, setClassRooms] = useState<ClassroomDtoEx[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();
  const { canEditClassrooms } = useAttendanceRights();

  useMetaTitle('Arbejdsområde fremmøde - Lokaler');

  async function loadClassRooms() {
    try {
      setIsLoading(true);
      const result = await classRoomClient.getClassRooms();
      setClassRooms(result.classRooms || []);
    } catch (error) {
      enqueueSnackbar('Der skete en fejl ved hentning af lokaler', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadClassRooms();
  }, []);

  usePostToTab(REFRESH_CLASSROOM_TABLE, () => loadClassRooms());

  function handleCreateClick() {
    window.open(`/${attendanceAreaPathName}/${getClassroomCreatePath()}`);
  }

  function handleRowClick(row: ClassroomDto) {
    window.open(`/${attendanceAreaPathName}/${getClassroomEditPath(row.classroomId)}`);
  }

  return (
    <DashboardLayout
      toolbar={
        <AreaToolbar
          mainItemGuid="43f675c0-9629-4ee2-be8f-d02012693834"
          prefixedText="Arbejdsområde fremmøde - "
          tools={
            canEditClassrooms
              ? [
                  <AreaToolbarButton key="add" icon={faPlus} color="primary" variant="text" iconPos="left" onClick={handleCreateClick}>
                    Opret nyt lokale
                  </AreaToolbarButton>,
                ]
              : []
          }
        />
      }
    >
      <Restricted
        actions={[
          { resourceType: ResourceTypes.AttendanceFacility, resourceOperation: ResourceOperations.Read },
          { resourceType: ResourceTypes.AttendanceFacility, resourceOperation: ResourceOperations.Write },
        ]}
      >
        <StartPageTable<ClassroomDto>
          title="Lokaler"
          context="Classrooms"
          universe="company"
          columns={classroomColumns}
          rows={classRooms}
          getRowId={row => row.classroomId}
          getRootEntityId={row => row.companyId}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          onRefresh={loadClassRooms}
        />
      </Restricted>
    </DashboardLayout>
  );
}
